<script setup lang="ts">
import BreadcrumbsSimple from '@/components/BreadcrumbsSimple.vue';
import AreaGuideMenu, { MenuItem, MenuItemChild } from '@/components/area-guide/AreaGuideMenu.vue';
import type { Area, Category } from '@ilteducation/content-ref-data-types';
import type { BreadcrumbsStep } from '@ilteducation/videos';
import { useLocale } from '@/plugins/translation';
import { computed, ref } from 'vue';
import { useDisplay } from 'vuetify';

defineProps<{
  activeSectionId: number;
  breadcrumbsSteps: BreadcrumbsStep[];
  currentSubject: Category | undefined;
  currentArea: Area | undefined;
  hasCarousel: boolean;
  hasTextbooks: boolean;
}>();

const emit = defineEmits<{
  scrollToAnchor: [item: MenuItem | MenuItemChild];
}>();

const { t, l } = useLocale();
const { mdAndUp, lgAndUp } = useDisplay();
const areaMenu = ref<typeof AreaGuideMenu>();
const activeSectionTitle = computed(() =>
  areaMenu?.value?.activeSectionTitle ? t(areaMenu?.value?.activeSectionTitle) : '',
);
</script>

<template>
  <v-navigation-drawer
    color="surface-container-lowest"
    v-if="mdAndUp"
    border
    rail
    :rail-width="lgAndUp ? 400 : 350"
    permanent
    class="area-guide--drawer"
    :aria-label="t(l.area_guide.label_navigation)"
  >
    <v-row align-content="space-between" class="fill-height px-6 py-2">
      <v-col cols="12">
        <p><BreadcrumbsSimple :steps="breadcrumbsSteps" /></p>
        <span class="text-on-surface greppa mt-0 mb-2">{{ t(l.area_guide.greppa) }}</span>
        <h1 class="area-title text-primary font-weight-bold mb-6">
          {{ currentArea?.name }}
        </h1>
        <AreaGuideMenu
          :has-carousel="hasCarousel"
          :has-textbooks="hasTextbooks"
          :active-section-id="activeSectionId"
          @scroll-to-anchor="(item) => emit('scrollToAnchor', item)"
        />
      </v-col>
      <v-col>
        <slot name="bottom"></slot>
      </v-col>
    </v-row>
  </v-navigation-drawer>
  <template v-else>
    <v-row>
      <v-col class="guide-header-mobile bg-white px-4 px-sm-8">
        <p class="mb-2">
          <BreadcrumbsSimple :steps="breadcrumbsSteps" />
        </p>
        <p class="text-on-surface greppa text-black mt-0 mb-2">{{ t(l.area_guide.greppa) }}</p>
        <h1 class="area-title-mobile text-primary font-weight-bold mb-6">{{ currentArea?.name }}</h1>
      </v-col>
    </v-row>
    <v-row class="expandable-menu">
      <v-col class="bg-transparent">
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-title color="white" class="bg-primary">
              <h3 class="mb-0">
                {{ activeSectionTitle }}
              </h3>
            </v-expansion-panel-title>
            <v-expansion-panel-text :eager="true">
              <AreaGuideMenu
                ref="areaMenu"
                :active-section-id="activeSectionId"
                :current-subject="currentSubject"
                :has-carousel="hasCarousel"
                :has-textbooks="hasTextbooks"
                @scroll-to-anchor="(item) => emit('scrollToAnchor', item)"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </template>
</template>

<style scoped lang="scss">
@use '@/foundations/theme/settings';

.expandable-menu {
  position: sticky;
  top: 64px;
  z-index: 10;
}

.guide-header-mobile {
  position: relative;
}

.area-guide--drawer {
  border-right: 10px solid rgb(var(--v-theme-primary));
  .area-title {
    font-size: 2rem;
    line-height: 1.2;
  }
}
.greppa {
  font-size: 1.375rem;
  font-family: settings.$heading-font-family;
}

.area-title-mobile {
  font-size: 1.5rem;
  line-height: 1.23;
}

p {
  font-size: 1rem;
  line-height: 1.25;
}
</style>
